import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, notifyIcon, number, quote } from "../../../../config";
import { appendFieldsFromObject, createFormData } from "../../../../helper/formDataHelper";
import { addUploadedEmailAttachments, deleteEmailAttachment } from "../../../../shared/services/workflow.services";
import { isImage, isPdf } from "../../../../shared/validators/validator";
import { bytesToSize, getNotification } from "../../../../utils/common";
import { downloadFile, getAttachmentObject, getAttachmentObjectEmail, getFileExtension, isValidSizeEmail } from "../Attachments/attachments.helper";
import { ImageModal } from "../Comments/ImageModal";
import "./EmailAttachments.scss";

/**
 * Email Attachments component for displaying email attachments
 * @param {Object} props
 * @param {Array} props.attachments - Array of attachment objects
 * @param {boolean} props.isDraft - Whether this is a draft email
 * @param {string} props.entityId - Entity ID for attachments (task ID, project ID, etc.)
 * @param {string} props.entityName - Entity name for attachments (Tasks, Project, etc.)
 * @returns JSX
 * @author Sejal
 */
const EmailAttachments = ({ attachments, isDraft, setAttachments, currentEmailId, addAttachments }) => {
  const [visible, setVisible] = useState(false);
  const [link, setLink] = useState("");
  const [fileSizeExceedMsg, setFileSizeExceedMsg] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [loaderType] = useState("infinite-spinner");
  const [hoveredAttachmentId, setHoveredAttachmentId] = useState(null);
  const [draftHoveredAttachmentId, setDraftHoveredAttachmentId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { emailType } = useSelector((state) => state.taskSidebar);

  /**
   * Returns attachment information including file size
   * @param {Object} attachment - The attachment object
   * @returns {string} Formatted file size string
   * @author Sejal
   */
  const getAttachmentInfo = (attachment) => {
    let showFileSize = bytesToSize(attachment.AttachmentSize);
    return `${showFileSize}`;
  };

  /**
   * Toggles the image dialog visibility and sets the link
   * @param {Object} attachment - The attachment to display
   * @author Sejal
   */
  const toggleDialog = (attachment) => {
    setVisible(!visible);
    setLink(attachment.AttachmentLink);
  };

  /**
   * Downloads an attachment file
   * @param {string} file - The file URL or path
   * @param {string} filename - The filename for download
   * @author Sejal
   */
  const downloadAttachmentFile = async (file, filename) => {
    try {
      await downloadFile(file, filename);
    } catch (error) {
      getNotification(label.DOWNLOAD_FAILED, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles mouse enter event for attachment hover
   * @param {string} attachmentId - The ID of the attachment being hovered
   * @author Sejal
   */
  const handleMouseEnter = (attachmentId) => {
    setHoveredAttachmentId(attachmentId);
  };

  /**
   * Handles mouse leave event for attachment hover
   * @author Sejal
   */
  const handleMouseLeave = () => {
    setHoveredAttachmentId(null);
  };

  /**
   * Handles mouse enter event for draft attachment hover
   * @param {string} attachmentId - The ID of the draft attachment being hovered
   * @author Sejal
   */
  const handleDraftMouseEnter = (attachmentId) => {
    setDraftHoveredAttachmentId(attachmentId);
  };

  /**
   * Handles mouse leave event for draft attachment hover
   * @author Sejal
   */
  const handleDraftMouseLeave = () => {
    setDraftHoveredAttachmentId(null);
  };

  /**
   * Handles view attachment click
   * @param {Object} attachment - The attachment to view
   * @author Sejal
   */
  const handleViewClick = (attachment) => {
    toggleDialog(attachment);
  };

  /**
   * Handles download attachment click
   * @param {Object} attachment - The attachment to download
   * @author Sejal
   */
  const handleDownloadClick = (attachment) => {
    downloadAttachmentFile(attachment?.AttachmentLink, attachment?.AttachmentName);
  };

  /**
   * Handle PDF view - opens PDF in new tab
   * @param {Object} attachment - The PDF attachment to view
   * @author Sejal
   */
  const handlePdfView = (attachment) => {
    window.open(attachment?.AttachmentLink, "_blank");
  };

  /**
   * Handle delete attachment (only for draft mode)
   * @param {Object} attachment - The attachment to delete
   * @author Sejal
   */
  const handleDeleteAttachment = useCallback(
    async (attachment) => {
      if (emailType == label.DRAFT_CONSTANT) {
        setAttachments((prevAttachments) => {
          const updatedAttachments = prevAttachments.filter((att) => att.Id !== attachment?.Id);
          return updatedAttachments;
        });
      } else {
        const payload = {
          attachmentId: attachment?.Id,
          userId: user?.id,
        };
        const response = await dispatch(deleteEmailAttachment(payload));
        if (response) {
          setAttachments((prevAttachments) => {
            const updatedAttachments = prevAttachments.filter((att) => att.Id !== attachment?.Id);
            return updatedAttachments;
          });
        }
      }
    },
    [emailType, user, dispatch]
  );

  /**
   * Add attachments for email attachments
   * @param {Event} event - The file input change event
   * @author Sejal
   */
  const addAttachmentHelper = useCallback(
    async (event) => {
      const files = event?.target?.files;
      if (!files.length) return;
      let filesToBeUploaded = [];
      for (let i = number.ZERO; i < files.length; i++) {
        if (!isValidSizeEmail(files[i].size, null)) {
          setFileSizeExceedMsg(quote.LIMIT_EXCEEDED);
          continue;
        }
        const imageUrl = URL.createObjectURL(files[i]);
        if (emailType !== label.DRAFT_CONSTANT) {
          filesToBeUploaded.push(files[i]);
        } else {
          const attachmentObject = getAttachmentObjectEmail(files[i], imageUrl, user, currentEmailId);
          filesToBeUploaded.push(attachmentObject);
        }
        setFileSizeExceedMsg("");
      }
      if (emailType == label.DRAFT_CONSTANT) {
        addAttachments(filesToBeUploaded);
        return;
      } else {
        if (filesToBeUploaded.length > 0) {
          const payload = { userId: user?.id, emailRef: currentEmailId?.id, emailRefType: currentEmailId?.EmailType, files: filesToBeUploaded };
          const formData = createFormData();
          appendFieldsFromObject(formData, payload);
          const uploadedAttachments = await dispatch(addUploadedEmailAttachments(formData));
          if (uploadedAttachments && uploadedAttachments.length > 0) {
            setAttachments((prevAttachments) => {
              const currentAttachments = Array.isArray(prevAttachments) ? prevAttachments : [];
              const newAttachments = [...currentAttachments, ...uploadedAttachments];
              return newAttachments;
            });
          }
        }
      }
    },
    [attachments, emailType, user, currentEmailId, addAttachments, dispatch]
  );

  // Drag and Drop handlers - only enabled for draft mode
  /**
   * Handles drag enter event for file drop functionality
   * @param {DragEvent} e - The drag enter event
   * @author Sejal
   */
  const handleDragEnter = useCallback(
    (e) => {
      if (!isDraft) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      setIsDragOver(true);
    },
    [isDraft]
  );

  /**
   * Handles drag leave event for file drop functionality
   * @param {DragEvent} e - The drag leave event
   * @author Sejal
   */
  const handleDragLeave = useCallback(
    (e) => {
      if (!isDraft) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragOver(false);
      }
    },
    [isDraft]
  );

  /**
   * Handles drag over event for file drop functionality
   * @param {DragEvent} e - The drag over event
   * @author Sejal
   */
  const handleDragOver = useCallback(
    (e) => {
      if (!isDraft) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [isDraft]
  );

  /**
   * Handles file drop event and processes dropped files
   * @param {DragEvent} e - The drop event
   * @author Sejal
   */
  const handleDrop = useCallback(
    (e) => {
      if (!isDraft) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        // Create a synthetic event object that matches what addAttachmentHelper expects
        const syntheticEvent = {
          target: {
            files: files,
          },
        };
        addAttachmentHelper(syntheticEvent);
      }
    },
    [isDraft, addAttachmentHelper]
  );

  /**
   * Returns JSX for attachment name
   * @param {Object} attachment
   * @returns {JSX}
   */
  const getAttachmentName = (attachment) => {
    if (isImage(attachment.AttachmentName)) {
      return (
        <>
          <div
            id='common-image-attachments-name-email'
            className=' cursor-pointer  date-time text-truncate'
            onClick={() => !isDraft && toggleDialog(attachment)}>
            {attachment.AttachmentName}
          </div>
          <div className='date-time text-muted text-small'> {getAttachmentInfo(attachment)} </div>
        </>
      );
    }

    if (isPdf(attachment.AttachmentName)) {
      return (
        <span>
          <div id='common-image-attachments-name-email' className=' cursor-pointer   date-time text-truncate'>
            {attachment.AttachmentName}
          </div>
          <div className='date-time text-muted text-small'> {getAttachmentInfo(attachment)} </div>
        </span>
      );
    }
    return (
      <span id='common-attachments-name-email'>
        <span className=' cursor-pointer   date-time text-truncate'>{attachment.AttachmentName}</span>

        <div className='date-time text-muted text-small'> {getAttachmentInfo(attachment)} </div>
      </span>
    );
  };

  return (
    <React.Fragment>
      <div className='d-flex flex-row justify-content-between '>
        <div
          className={`attachment-container  position-relative ${isDragOver ? "drag-over" : ""}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}>
          {attachments?.length > number.ZERO && (
            <div className={`file-uploaded-wrapper project-dashboard-attachment d-flex flex-wrap`}>
              {/* {showLoader && <Loader type={loaderType} className='infinite-spinner' />} */}
              {fileSizeExceedMsg && <p className={`red  text-right`}>{fileSizeExceedMsg}</p>}
              {attachments.map((attachment, index) => {
                const fileExtension = getFileExtension(attachment.AttachmentName);
                const isHovered = hoveredAttachmentId === attachment.Id;
                const isDraftHovered = draftHoveredAttachmentId === attachment.Id;
                const isImageFile = isImage(attachment.AttachmentName);

                return (
                  <div
                    title={attachment.AttachmentName}
                    className='attachment-item d-flex align-items-center mb-10 position-relative  '
                    key={attachment.Id}
                    onMouseEnter={() => (isDraft ? handleDraftMouseEnter(attachment.Id) : handleMouseEnter(attachment.Id))}
                    onMouseLeave={() => (isDraft ? handleDraftMouseLeave() : handleMouseLeave())}>
                    <div className='file-icon-svg d-flex mr-2'>
                      <FileIcon
                        extension={fileExtension}
                        {...(isPdf(attachment.AttachmentName)
                          ? defaultStyles.pdf
                          : isImage(attachment.AttachmentName)
                          ? defaultStyles.png
                          : defaultStyles[fileExtension] || defaultStyles.txt)}
                        color='#0066cc'
                        glyphColor='white'
                      />
                    </div>
                    <div className='file-name d-flex flex-column w-100'>{getAttachmentName(attachment)}</div>

                    {/* Show cross/delete button for draft mode on hover */}
                    {isDraft && isDraftHovered && (
                      <div
                        className='attachment-delete-icon  d-flex align-items-center justify-content-center '
                        onClick={() => handleDeleteAttachment(attachment)}>
                        {icon.CLOSE}
                      </div>
                    )}

                    {/* Show view/download actions for non-draft mode */}
                    {!isDraft && (
                      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                        {isHovered && (
                          <div className='attachment-action-overlay d-flex align-items-center justify-content-center'>
                            {isImageFile && (
                              <div className='action-icon mr-3 cursor-pointer' onClick={() => handleViewClick(attachment)} title='View'>
                                {icon.FOLLOWED}
                              </div>
                            )}
                            {isPdf(attachment.AttachmentName) && (
                              <div className='action-icon mr-3 cursor-pointer' onClick={() => handlePdfView(attachment)} title='View PDF'>
                                {icon.FOLLOWED}
                              </div>
                            )}
                            <div className='action-icon cursor-pointer' onClick={() => handleDownloadClick(attachment)} title='Download'>
                              {icon.IMPORT_FILE}
                            </div>
                          </div>
                        )}
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Drop zone indicator when dragging - only for draft mode */}
          {isDragOver && isDraft && (
            <div className='drop-zone-overlay'>
              <div className='drop-zone-content'>
                {icon.CLOUD_UPLOAD}
                <p className='drop-text'>Drop files here to attach</p>
              </div>
            </div>
          )}
        </div>

        {/* Add Attachment Button - only show in draft mode */}
        {isDraft && (
          <div className='mt-2 mr-2'>
            <label htmlFor='email-attachment-add-button' className='add-attachment-button btn btn-sm btn-block btn-primary'>
              Add Attachment
            </label>
            <input id='email-attachment-add-button' title='' type='file' onChange={addAttachmentHelper} className='d-none' multiple />
          </div>
        )}
      </div>
      {visible && link && <ImageModal tag={`<img src=${link}>`} visible={true} link={link} toggleVisible={toggleDialog} />}
    </React.Fragment>
  );
};

export default EmailAttachments;
